import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '../../StatusPill/StatusPill';
import { VersionBadge } from '../../VersionBadge/VersionBadge';
import { FileTypeIcon } from '../../FileTypeIcon/FileTypeIcon';
import { formatFileSize } from '@/utils/formatFileSize';
import { formatProjectDate } from '@/utils/formatProjectDate';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { RenameInput } from '../../RenameInput/RenameInput';
import { PendingSpinner } from '../../PendingSpinner/PendingSpinner';
import type { IFileItemProps } from '../itemProps';
import { useDragItem } from '../hooks/useDragItem';
import styles from './DocumentTable.module.css';

export function DocumentRow({
  doc, isSelected, isMenuTarget = false, rename, isRejected, onSelect, onContextMenu,
}: IFileItemProps) {
  const { t } = useTranslation();
  const isRenaming = Boolean(rename);
  // A rename still saving: the row's id is about to change, so it can't be opened, dragged or right-clicked yet.
  const isSaving = Boolean(doc.saving);
  const locked = isRenaming || isSaving;
  /* Document stamps render in the PROJECT's zone, never the viewer's.
     Undefined only during the bootstrap window before the working site
     resolves — an em-dash then, rather than a date in a guessed zone. */
  const tz = useSelector(selectSiteTz);
  const { setNodeRef, attributes, listeners, style } = useDragItem(doc.id, 'document', locked);

  // The oldest version this user can act on — versions arrive newest-first, so
  // the last match is the one whose turn it is.
  const awaiting = [...doc.versions].reverse().find((v) => v.canSubmit || v.canApprove || v.canReject);

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${styles.row} ${isSelected || isMenuTarget ? styles.rowSelected : ''} ${isRejected ? styles.dragRejected : ''} ${isSaving ? styles.rowSaving : ''}`}
      onClick={() => !locked && onSelect(isSelected ? null : doc.id)}
      onContextMenu={(e) => (isSaving ? e.preventDefault() : onContextMenu(e, doc.id))}
      onKeyDown={(e) => {
        if (!locked && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect(isSelected ? null : doc.id);
        }
      }}
      aria-selected={isSelected}
      aria-busy={isSaving || undefined}
      data-tour="documents-doc"
      {...attributes}
      {...listeners}
    >
      <td className={`${styles.td} ${styles.colName}`}>
        <span className={styles.nameCell}>
          <FileTypeIcon doc={doc} />
          <span>
            {rename ? (
              <RenameInput {...rename} />
            ) : (
              <span className={styles.docName}>{doc.name}{isSaving && <PendingSpinner />}</span>
            )}
          </span>
        </span>
      </td>
      <td className={`${styles.td} ${styles.colDesc}`}>
        <span className={styles.descText}>{doc.description.trim() || '-'}</span>
      </td>
      <td className={`${styles.td} ${styles.colVersion}`}>
        <VersionBadge version={doc.version} />
      </td>
      <td className={`${styles.td} ${styles.colStatus}`}>
        <span className={styles.statusCell}>
          <StatusPill status={doc.status} />
          {/* The row presents the approved version, so a version waiting on
              this user would otherwise be invisible from the table. */}
          {awaiting && (
            <span className={styles.awaitingChip}>
              {t(
                awaiting.canSubmit ? 'documents.approval.needsSubmit' : 'documents.approval.needsDecision',
                { version: awaiting.version },
              )}
            </span>
          )}
        </span>
      </td>
      <td className={`${styles.td} ${styles.colSize}`}>
        <span className={styles.sizeText}>{formatFileSize(doc.sizeBytes)}</span>
      </td>
      <td className={`${styles.td} ${styles.colUpdated}`}>
        <span className={styles.updatedCell}>
          <span className={styles.updatedDate}>{tz ? formatProjectDate(doc.updatedAt, tz) : '—'}</span>
          <span className={styles.updatedBy}>{doc.updatedBy}</span>
        </span>
      </td>
    </tr>
  );
}